/* @flow */
import * as React from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import _ from 'lodash';

import styles from './styles.module.scss';
import placeholderImage from '../../assets/images/protones-placeholder-1.png';
import ActionButton from '../../components/ActionButton';
import Aggregation from '../../components/Aggregation';
import Checkbox from '../../components/Checkbox';
import DataLoader from '../../components/DataLoader';
import IconButton from '../../components/IconButton';
import ErrorAwareImage from '../../components/ErrorAwareImage';
import CoverCard from '../../components/CoverCard';
import Sidebar, { SidebarSection } from '../../components/Sidebar';
import ScrollableContent from '../../components/ScrollableContent';
import SliderList from '../../components/SliderList';
import actions from '../../redux/actions';
import { getCategories } from '../../redux/reducers/categories';
import {
  getFeatured,
  getFeaturedNewsSearchParams,
  getSubmittedFeaturedNewsSearchParams,
  getSections,
  getAggregations,
  getSelectedCategoryIds,
} from '../../redux/reducers/featuredNews';
import common from '../../utils/common';
import protonesUtils from '../../utils/protones';

import type {
  Translate,
  ReduxState,
  Fetchable,
  Featured,
  Category,
  MediaSearchResults,
  MediaSearchFullParams,
  SimplifiedMedia,
  Aggregations,
  MediaAggregationKey,
  ReactRouterProps,
} from '../../types';

type Props = ReactRouterProps & {
  featuredId: number,
  params: MediaSearchFullParams,
  submittedParams: MediaSearchFullParams,
  featured: Fetchable<Featured>,
  categories: Fetchable<Category[]>,
  aggregations: Fetchable<Aggregations<MediaAggregationKey>>,
  sections: Fetchable<MediaSearchResults>,
  pageScroll: number,
  expandedCategories: {
    [categoryId: number]: boolean,
  },
  slideIndexByCategory: {
    [categoryId: number]: number,
  },
  fetchFeatured: typeof actions.fetchFeatured,
  setFeaturedMediaSearch: typeof actions.setFeaturedMediaSearch,
  performFeaturedMediaSearch: typeof actions.performFeaturedMediaSearch,
  featuredMediaSetPageScroll: typeof actions.featuredMediaSetPageScroll,
  featuredMediaToggleExpandCategory: typeof actions.featuredMediaToggleExpandCategory,
  featuredMediaSetCategorySlide: typeof actions.featuredMediaSetCategorySlide,
  t: Translate,
};

type State = {
  slideIndexByCategory: {
    [categoryId: number]: number,
  },
  tagsSearchText: string,
};

const mapStateToProps = (state: ReduxState, ownProps: Props) => {
  const featuredId = parseInt(ownProps.match.params.featuredId);
  return {
    featuredId,
    featured: getFeatured(state),
    aggregations: getAggregations(state),
    params: { ...getFeaturedNewsSearchParams(state), featuredId },
    submittedParams: { ...getSubmittedFeaturedNewsSearchParams(state), featuredId },
    categories: getCategories(state),
    sections: getSections(state),
    selectedCategoryIds: getSelectedCategoryIds(state),
    pageScroll: state.featuredNews.pageScroll,
    expandedCategories: state.featuredNews.expandedCategories,
    slideIndexByCategory: state.featuredNews.slideIndexByCategory,
  };
};

class FeaturedMedia extends React.Component<Props, State> {
  state = {
    slideIndexByCategory: this.props.slideIndexByCategory,
    tagsSearchText: '',
  };

  scrollContainer: any = React.createRef();
  pageScroll: number = 0;

  componentDidMount() {
    if (!this.props.featured.data || this.props.featured.data.id !== this.props.featuredId) {
      this.props.fetchFeatured(this.props.featuredId);
      this.props.performFeaturedMediaSearch(this.props.params);
    }
    if (this.scrollContainer.current) {
      this.scrollContainer.current.scrollTo(0, this.props.pageScroll);
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.categories.data && !_.isEqual(prevProps.params, this.props.params)) {
      this.props.performFeaturedMediaSearch(this.props.params);
    }
  }

  componentWillUnmount() {
    this.props.featuredMediaSetPageScroll(this.pageScroll);
  }

  _handlePageScroll = (event: any) => {
    const element = event.target;
    const pageScroll = element.scrollTop;
    this.pageScroll = pageScroll;
  };

  _handleRefreshFeaturedMedia = () => {
    this.props.fetchFeatured(this.props.featuredId);
    if (this.props.categories.data) {
      this.props.performFeaturedMediaSearch(this.props.params);
    }
  };

  _handleMediaClick = (media: SimplifiedMedia) => {
    protonesUtils.goTo(this.props, protonesUtils.routes.medias.get(media.id));
  };

  _setSearch = params => {
    return this.props.performFeaturedMediaSearch(params);
  };

  _toggleCategory = (categoryId: number) => {
    const { params } = this.props;
    const categoryIds = common.toggle(params.categoryIds, categoryId);
    this.props.setFeaturedMediaSearch({
      ...params,
      categoryIds,
    });
  };

  _toggleCategories = () => {
    const { params, categories } = this.props;
    const allCategoryIds = (categories.data || []).map(c => c.id);
    const categoryIds = params.categoryIds || [];
    const isFilterGroupActive = categoryIds && categoryIds.length > 0;
    const nextParams = {
      ...params,
      categoryIds: isFilterGroupActive ? [] : allCategoryIds,
    };
    this.props.setFeaturedMediaSearch(nextParams);
  };

  _getIsFilterActive = (filter: MediaAggregationKey, value: any) => {
    const { params } = this.props;
    let { filters } = params;
    if (filters == null || filters[filter] == null || !filters[filter].includes(value)) {
      return false;
    }
    return true;
  };

  _addFilterAndSubmit = (filter: MediaAggregationKey, value: any) => {
    const { params } = this.props;
    let { filters } = params;
    if (filters == null) {
      filters = { [filter]: [value] };
    } else if (filters[filter] == null) {
      filters[filter] = [value];
    } else {
      filters[filter] = _.uniq([...filters[filter], value]);
    }
    const nextParams = {
      ...params,
      filters,
    };
    this._setSearch(nextParams);
    return nextParams;
  };

  _removeFilterAndSubmit = (filter: MediaAggregationKey, value: any) => {
    const { params } = this.props;
    let { filters } = params;
    if (filters == null || filters[filter] == null || !filters[filter].includes(value)) {
      return params;
    }
    filters = filters || {};
    filters = {
      ...filters,
      [filter]: (filters[filter] || []).filter(val => val !== value),
    };
    const nextParams = {
      ...params,
      filters,
    };
    this._setSearch(nextParams);
    return nextParams;
  };

  _toggleFilterAndSubmit = (filter: MediaAggregationKey, value: any) => {
    const isFilterActive = this._getIsFilterActive(filter, value);
    if (isFilterActive) {
      return this._removeFilterAndSubmit(filter, value);
    } else {
      return this._addFilterAndSubmit(filter, value);
    }
  };

  _removeFilterGroupAndSubmit = (filter: MediaAggregationKey) => {
    const { params } = this.props;
    let { filters } = params;
    filters = _.omit(filters, filter) || {};
    const nextParams = {
      ...params,
      filters,
    };
    this._setSearch(nextParams);
    return nextParams;
  };

  _addFilterGroupAndSubmit = (filter: MediaAggregationKey) => {
    const { aggregations, params } = this.props;
    const buckets = _.get(aggregations, `data.${filter}.buckets`, []);
    const filterValues = buckets.map(b => b.key);
    const filters = {
      ...(params.filters || {}),
      [filter]: filterValues,
    };
    const nextParams = {
      ...params,
      filters,
    };
    this._setSearch(nextParams);
    return nextParams;
  };

  _getIsFilterGroupActive = (filter: MediaAggregationKey) => {
    const { params } = this.props;
    let { filters } = params;
    if (filters == null || filters[filter] == null || filters[filter].length === 0) {
      return false;
    }
    return true;
  };

  _toggleFilterGroupAndSubmit = (filter: MediaAggregationKey) => {
    const isFilterGroupActive = this._getIsFilterGroupActive(filter);
    if (isFilterGroupActive) {
      return this._removeFilterGroupAndSubmit(filter);
    } else {
      return this._addFilterGroupAndSubmit(filter);
    }
  };

  _resetFiltersAndSubmit = () => {
    const { performFeaturedMediaSearch, setFeaturedMediaSearch, params } = this.props;
    const nextParams = {
      ...params,
      categoryIds: [],
      excludeProductionMusic: false,
      filters: {},
    };
    this.setState({ tagsSearchText: '' });
    setFeaturedMediaSearch(nextParams);
    performFeaturedMediaSearch(nextParams);
  };

  _setFiltersAndSubmit = (filters: { [MediaAggregationKey]: any[] }) => {
    const { performFeaturedMediaSearch, setFeaturedMediaSearch, params } = this.props;
    const nextParams = {
      ...params,
      filters,
    };
    setFeaturedMediaSearch(nextParams);
    performFeaturedMediaSearch(nextParams);
  };

  _increaseFilterGroupPagination = (filter: MediaAggregationKey) => {
    const { params } = this.props;
    const filterPagination = _.get(params, `filtersMeta.${filter}.pagination`, 10);
    const nextParams = _.merge({}, params);
    _.set(nextParams, `filtersMeta.${filter}.pagination`, filterPagination + 10);
    this._setSearch(nextParams);
  };

  _toggleExcludeProductionMusic = () => {
    const { params } = this.props;
    const nextParams = {
      ...params,
      excludeProductionMusic: !params.excludeProductionMusic,
    };
    this._setSearch(nextParams);
  };

  _renderExcludeProductionMusic = () => {
    const checked = !!this.props.submittedParams.excludeProductionMusic;
    return (
      <div className={styles.aggregation}>
        <div className={styles.aggregationBuckets}>
          <div
            onClick={e => {
              this._toggleExcludeProductionMusic();
              e.preventDefault();
            }}
            className={styles.aggregationBucket}
          >
            <Checkbox
              small={true}
              value={'excludeProductionMusic'}
              label={this.props.t('EXCLUDE_PRODUCTION_MUSIC')}
              labelAfter={true}
              checked={checked}
            />
          </div>
        </div>
      </div>
    );
  };

  _renderAggregations = data => {
    const {
      submittedParams: { categoryIds },
      t,
    } = this.props;
    const { tagsSearchText } = this.state;
    const filters = { ...this.props.submittedParams.filters, categories: categoryIds };
    const {
      categories,
      tags,
      labels,
      performers,
      editors,
      authors,
      years,
      catalogs,
      collectings,
    } = data;
    return (
      <div>
        {this._renderExcludeProductionMusic()}
        <Aggregation
          name="categories"
          label={t('CATEGORIES')}
          categories={this.props.categories.data || []}
          buckets={categories.buckets}
          selectedBuckets={_.get(filters, 'categories', [])}
          onFilterToggle={(_, val) => this._toggleCategory(val)}
          onAllFiltersToggle={this._toggleCategories}
          showAll={true}
        />
        <Aggregation
          name="performers"
          label={t('PERFORMER')}
          buckets={performers.buckets}
          selectedBuckets={_.get(filters, 'performers', [])}
          onFilterToggle={this._toggleFilterAndSubmit}
          onAllFiltersToggle={this._toggleFilterGroupAndSubmit}
          onLoadMore={this._increaseFilterGroupPagination}
        />
        <Aggregation
          name="years"
          label={t('YEAR')}
          buckets={years.buckets}
          selectedBuckets={_.get(filters, 'years', [])}
          onFilterToggle={this._toggleFilterAndSubmit}
          onAllFiltersToggle={this._toggleFilterGroupAndSubmit}
          onLoadMore={this._increaseFilterGroupPagination}
        />
        <Aggregation
          name="labels"
          label={t('LABEL')}
          buckets={labels.buckets}
          selectedBuckets={_.get(filters, 'labels', [])}
          onFilterToggle={this._toggleFilterAndSubmit}
          onAllFiltersToggle={this._toggleFilterGroupAndSubmit}
          onLoadMore={this._increaseFilterGroupPagination}
        />
        <Aggregation
          name="catalogs"
          label={t('CATALOG')}
          buckets={catalogs.buckets}
          selectedBuckets={_.get(filters, 'catalogs', [])}
          onFilterToggle={this._toggleFilterAndSubmit}
          onAllFiltersToggle={this._toggleFilterGroupAndSubmit}
          onLoadMore={this._increaseFilterGroupPagination}
        />
        <Aggregation
          name="authors"
          label={t('AUTHORS')}
          buckets={authors.buckets}
          selectedBuckets={_.get(filters, 'authors', [])}
          onFilterToggle={this._toggleFilterAndSubmit}
          onAllFiltersToggle={this._toggleFilterGroupAndSubmit}
          onLoadMore={this._increaseFilterGroupPagination}
        />
        <Aggregation
          name="editors"
          label={t('EDITORS')}
          buckets={editors.buckets}
          selectedBuckets={_.get(filters, 'editors', [])}
          onFilterToggle={this._toggleFilterAndSubmit}
          onAllFiltersToggle={this._toggleFilterGroupAndSubmit}
          onLoadMore={this._increaseFilterGroupPagination}
        />
        <Aggregation
          name="tags"
          label={t('TAGS')}
          buckets={tags.buckets}
          selectedBuckets={_.get(filters, 'tags', [])}
          onFilterToggle={this._toggleFilterAndSubmit}
          onAllFiltersToggle={this._toggleFilterGroupAndSubmit}
          canSearchFilters={true}
          searchText={tagsSearchText}
          onSearchTextChange={text => this.setState({ tagsSearchText: text })}
        />
        <Aggregation
          name="collectings"
          label={t('COLLECTING')}
          buckets={collectings.buckets}
          selectedBuckets={_.get(filters, 'collectings', [])}
          onFilterToggle={this._toggleFilterAndSubmit}
          onAllFiltersToggle={this._toggleFilterGroupAndSubmit}
          onLoadMore={this._increaseFilterGroupPagination}
        />
      </div>
    );
  };

  _renderFeatured = data => {
    const {
      featured: { fetching },
    } = this.props;
    return (
      <div className={!fetching ? styles.sidebarFilters : styles.sidebarFiltersLoading}>
        <ErrorAwareImage
          style={{ width: 192, heigth: 192 }}
          src={data.coverUrl || placeholderImage}
          alt={data.name}
        />
        <h2>{data.name}</h2>
      </div>
    );
  };

  _renderNoData = () => {
    return null;
  };

  _renderCategory = (category, data) => {
    const { t } = this.props;
    const { slideIndexByCategory } = this.state;
    const isCategoryExpanded = !!this.props.expandedCategories[category.id];
    return data && data.hits.length > 0 ? (
      <div style={{ marginBottom: '3em' }}>
        <SliderList
          key={category.id}
          title={category.description}
          render={item => (
            <CoverCard
              title={item.title}
              subtitle={
                item.performers && item.performers.length > 0
                  ? protonesUtils.getPersonLabel(item.performers[0])
                  : t('NO_ARTIST')
              }
              description={[item.year, item.label && item.label.name].filter(_ => _).join(' - ')}
              imgSrc={protonesUtils.getMediaCoverUrl(item)}
              onClick={() => this._handleMediaClick(item)}
            />
          )}
          items={data.hits}
          renderNoData={this._renderNoData}
          isExpanded={isCategoryExpanded}
          onExpandClick={() => this.props.featuredMediaToggleExpandCategory(category.id)}
          onCollapseClick={() => this.props.featuredMediaToggleExpandCategory(category.id)}
          onSlideChange={slideIndex =>
            this.props.featuredMediaSetCategorySlide(category.id, slideIndex)
          }
          initialSlide={slideIndexByCategory[category.id] || 0}
        />
      </div>
    ) : (
      this._renderNoData()
    );
  };

  render() {
    const { aggregations, categories, featured, submittedParams, sections, t } = this.props;
    const allCategories = categories.data || [];
    const hasActiveFilters = _.values(submittedParams.filters).some(f => !_.isEmpty(f));
    const canResetFilters =
      (submittedParams.categoryIds || []).length > 0 ||
      hasActiveFilters ||
      !!submittedParams.excludeProductionMusic;
    return (
      <div className={styles.root}>
        <Sidebar
          title={t('FEATURED')}
          onRefresh={this._handleRefreshFeaturedMedia}
          loading={featured.fetching || aggregations.fetching || sections.fetching}
        >
          <div className={styles.sidebarSection}>
            <DataLoader
              loading={featured.fetching}
              error={featured.error}
              data={featured.data}
              render={this._renderFeatured}
            />
          </div>
          <SidebarSection
            title={t('FILTERS')}
            disabled={!canResetFilters}
            buttons={[
              <IconButton
                icon={'reset_filter'}
                tooltip={t('RESET_FILTERS')}
                disabled={!canResetFilters}
                onClick={this._resetFiltersAndSubmit}
              />,
            ]}
          >
            <div className={styles.sidebarSection}>
              <DataLoader
                loading={aggregations.fetching}
                error={aggregations.error}
                data={aggregations.data}
                render={this._renderAggregations}
              />
            </div>
          </SidebarSection>
        </Sidebar>
        <ScrollableContent ref={this.scrollContainer} onScroll={this._handlePageScroll}>
          <div className={styles.main}>
            <div className={styles.close}>
              <ActionButton iconName={'close'} onClick={this.props.history.goBack} />
            </div>
            {sections.fetching ? (
              <DataLoader loading />
            ) : (
              allCategories.map(category => {
                const data = _.get(sections, `data[${category.id}]`);
                return (
                  <DataLoader
                    loading={sections.fetching}
                    error={sections.error}
                    data={data}
                    render={data => this._renderCategory(category, data)}
                  />
                );
              })
            )}
          </div>
        </ScrollableContent>
      </div>
    );
  }
}

export default withTranslation()(connect(mapStateToProps, actions)(FeaturedMedia));
